// BookingDrawer — a side-sheet quick view of a booking, opened from the bookings
// list and the calendar booking bars (the user-approved "drawer + link to the
// full page" pattern). Re-authored from design/project/ops/bookings.jsx's
// BookingDrawer, wired to real data (bookingQuery / paymentScheduleQuery).
//
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingMoney, bookingQuery, isCancelEligible, type BookingDetail } from '@/lib/bookings';
import { apartmentsQuery, type Apartment } from '@/lib/apartments';
import { usePermissions } from '@/lib/me';
import { cancelBooking, cancelErrorToast, cancelSuccessMessage } from '@/lib/cancel';
import { posthog } from '@/lib/posthog';
import { errorMessage } from '@/lib/errors';
import { formatDate, formatMoney } from '@/lib/format';
import { daysBetweenYMD } from '@/lib/calendar';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Tabs } from '@/components/ui/tabs';
import { BookingStatusBadge, BookingSourceBadge } from '@/components/StatusBadges';
import { useConfirm } from '@/components/DialogProvider';
import { useToast } from '@/components/ToastProvider';
import { PaymentsCard } from '@/components/booking/PaymentsCard';
import { RevisionsTimeline } from '@/components/booking/RevisionsTimeline';

type DrawerTab = 'overview' | 'timeline' | 'payments' | 'notes';

const TABS: { value: DrawerTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'payments', label: 'Payments' },
  { value: 'notes', label: 'Notes' },
];

export interface BookingDrawerProps {
  /** Booking id to show; null closes the drawer. */
  bookingId: string | null;
  onClose: () => void;
}

export function BookingDrawer({ bookingId, onClose }: BookingDrawerProps) {
  const [tab, setTab] = useState<DrawerTab>('overview');
  const [cancelPending, setCancelPending] = useState(false);
  const queryClient = useQueryClient();
  const askConfirm = useConfirm();
  const { toast } = useToast();

  // Reset to Overview whenever a different booking opens.
  useEffect(() => {
    setTab('overview');
  }, [bookingId]);

  const open = bookingId !== null;
  const {
    data: booking,
    isLoading,
    error,
  } = useQuery({ ...bookingQuery(bookingId ?? ''), enabled: open });
  const { data: apartments } = useQuery(apartmentsQuery);
  // Gate on effective permissions, the same actions the backend authorizes —
  // matches the full booking-detail page's pattern (bookings.$id.tsx).
  const { can } = usePermissions();
  const userCanCancel = can('bookings.cancel');

  const apt: Apartment | undefined = booking
    ? (apartments ?? []).find((a) => a.id === booking.apartment_id)
    : undefined;

  // Deterministic cancel for a booking WE own (ARI-only direct/manual) — flip +
  // recompute availability + conditional refund (ADR-034). OTA/Channex bookings
  // are not eligible (they cancel in Channex).
  async function onCancelBooking() {
    if (!booking) return;
    const bk = booking;
    const aptLabel = apt ? apt.title : bk.apartment_id;
    const ok = await askConfirm({
      title: 'Cancel this booking?',
      body:
        `This cancels the booking and releases the nights. If a payment was captured, the guest is refunded.\n\n` +
        `${aptLabel}\n` +
        `${bk.checkin_date} → ${bk.checkout_date}\n` +
        `${formatMoney(bk.total_amount_minor, bk.currency)}` +
        (bk.guest_email ? `\n${bk.guest_email}` : '') +
        `\n\nThis cannot be undone.`,
      confirmLabel: 'Cancel booking',
      cancelLabel: 'Keep',
      destructive: true,
    });
    if (!ok) return;

    setCancelPending(true);
    try {
      const res = await cancelBooking(bk.id);
      posthog.capture?.('pms_action', { action: 'booking_cancelled' });
      toast({
        kind: 'success',
        title: 'Booking cancelled',
        message: cancelSuccessMessage(res),
      });
      await queryClient.invalidateQueries({ queryKey: bookingQuery(bk.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['ari', 'calendar'] });
      onClose();
    } catch (err) {
      toast(cancelErrorToast(err));
    } finally {
      setCancelPending(false);
    }
  }

  const cancelEligible = !!booking && isCancelEligible(booking) && userCanCancel;

  const title = booking ? booking.guest_name : 'Booking';
  const description = booking
    ? `#${booking.id.slice(0, 8)} · ${booking.ota_reservation_code ?? '—'} · ${apt?.title ?? '…'}`
    : undefined;

  return (
    <Dialog
      open={open}
      side
      onClose={onClose}
      title={title}
      description={description}
      footer={
        cancelEligible ? (
          <Button variant="destructive" loading={cancelPending} onClick={onCancelBooking}>
            Cancel booking
          </Button>
        ) : undefined
      }
    >
      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(error)}
        </p>
      )}

      {booking && (
        <div className="flex flex-col gap-4">
          {/* Status row */}
          <div className="flex flex-wrap items-center gap-2">
            <BookingStatusBadge status={booking.status} />
            <BookingSourceBadge source={booking.source} />
          </div>

          <Tabs variant="pill" value={tab} onChange={(v) => setTab(v as DrawerTab)} tabs={TABS} />

          <div>
            {tab === 'overview' && <OverviewTab booking={booking} />}
            {tab === 'timeline' && <RevisionsTimeline revisions={booking.revisions} />}
            {tab === 'payments' && <PaymentsCard booking={booking} />}
            {tab === 'notes' && <NotesTab booking={booking} />}
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ─── Overview tab ───────────────────────────────────────────────────────────

function OverviewTab({ booking }: { booking: BookingDetail }) {
  const nights = daysBetweenYMD(booking.checkin_date, booking.checkout_date);
  // Same money rows as the booking detail card, from the same definition.
  const { commission, taxOnTop, payout } = bookingMoney(booking);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Check-in">{formatDate(booking.checkin_date)}</Field>
        <Field label="Check-out">{formatDate(booking.checkout_date)}</Field>
        <Field label="Nights">{nights}</Field>
        <Field label="Guests">
          <span className="tabular-nums">
            {booking.adults} adult{booking.adults === 1 ? '' : 's'}
            {booking.children > 0
              ? ` · ${booking.children} ${booking.children === 1 ? 'child' : 'children'}${
                  booking.children_ages?.length ? ` (${booking.children_ages.join(', ')})` : ''
                }`
              : ''}
            {booking.infants > 0 ? ` · ${booking.infants} infant${booking.infants === 1 ? '' : 's'}` : ''}
          </span>
        </Field>
      </div>

      <div className="border-t border-[var(--border-soft)] pt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted">Total</span>
          <span className="text-lg font-semibold tabular-nums">
            {formatMoney(booking.total_amount_minor, booking.currency)}
          </span>
        </div>
        <div className="mt-2 space-y-1 text-sm">
          {taxOnTop !== null && (
            <div className="flex items-center justify-between">
              <span className="text-muted">Tourism tax · collected on arrival</span>
              <span className="tabular-nums text-muted">
                {formatMoney(taxOnTop, booking.currency)}
              </span>
            </div>
          )}
          {commission !== null && payout !== null && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted">OTA commission</span>
                <span className="tabular-nums">
                  {formatMoney(commission, booking.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted">Payout</span>
                <span className="font-semibold tabular-nums">
                  {formatMoney(payout, booking.currency)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <Link
        to="/bookings/$id"
        params={{ id: booking.id }}
        className="inline-flex w-full items-center justify-center gap-1 rounded-[var(--radius-control)] border border-[var(--border-default)] bg-card px-4 py-2 text-sm font-semibold text-[var(--text-heading)] transition-colors hover:bg-[var(--surface-sunken)]"
      >
        Open full booking →
      </Link>
    </div>
  );
}

// ─── Notes tab ──────────────────────────────────────────────────────────────

function NotesTab({ booking }: { booking: BookingDetail }) {
  const si = booking.service_info;
  const requests: string[] = [];
  if (si) {
    if (si.early_checkin) requests.push('Early check-in');
    if (si.late_checkout) requests.push('Late check-out');
    if (si.parking_needed) requests.push(`Parking${si.parking_note ? ` (${si.parking_note})` : ''}`);
    if (si.pets) requests.push('Pets');
    if (si.crib > 0) requests.push(`Crib ×${si.crib}`);
    if (si.high_chair > 0) requests.push(`High chair ×${si.high_chair}`);
  }
  const hasContent = !!si?.notes || requests.length > 0;

  if (!hasContent) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">No operational details recorded yet.</p>
        <Link
          to="/bookings/$id"
          params={{ id: booking.id }}
          className="text-sm text-primary hover:underline"
        >
          Edit on full booking →
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {requests.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Service requests
          </h3>
          <ul className="flex flex-wrap gap-1.5">
            {requests.map((r) => (
              <li
                key={r}
                className="rounded-full border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-2.5 py-0.5 text-xs"
              >
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Internal notes
        </h3>
        {si?.notes ? (
          <p className="whitespace-pre-wrap text-sm text-foreground">{si.notes}</p>
        ) : (
          <p className="text-sm text-muted">No internal notes.</p>
        )}
      </div>

      <Link
        to="/bookings/$id"
        params={{ id: booking.id }}
        className="text-sm text-primary hover:underline"
      >
        Edit on full booking →
      </Link>
    </div>
  );
}

// ─── Small field primitive (drawer-local) ───────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

export default BookingDrawer;
